import axios from "axios";
import {
    AdminUserAnalyticsResponse,
    Chapter,
    ChapterDetailsResponse,
    CodeAttempt,
    CodeTaskAttemptResponse, CodeTaskImportResultResponse,
    CodeTaskListResponse,
    CodeTaskRequest, Company, CompanyRequest,
    Course,
    CourseDetailsResponse, CourseLogo, CourseLogoRequest, CourseRequest,
    CreatePracticeTaskRequest,
    CreateUserRequest,
    Lesson,
    LessonDetailsResponse,
    PracticeTaskDetailsResponse,
    PracticeTaskListResponse,
    UpdatePracticeTaskRequest,
    UpdateUserRequest,
    UserProfile,
    UserProfileResponse,
    UserRole
} from "../types";
import {refreshAccessToken} from "../helper/auth";
import {CodeExecutionResponse} from "../types/coderunner";
import {ApiError} from "../types/api-errors";

export const API_BASE_URL = "/api";

const api = axios.create({
    baseURL: API_BASE_URL,
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("accessToken");
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest?._retry) {
            originalRequest._retry = true;

            try {
                const newToken = await refreshAccessToken();
                originalRequest.headers = originalRequest.headers ?? {};
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                localStorage.removeItem("accessToken");
                localStorage.removeItem("refreshToken");
                window.location.href = "/login";
                return Promise.reject(refreshError);
            }
        }

        const data = error?.response?.data;

        if (data && typeof data === "object" && typeof data.message === "string") {
            const apiError: ApiError = {
                status: data.status,
                code: data.code,
                message: data.message,
            };
            return Promise.reject(apiError);
        }

        if (!error.response) {
            const apiError: ApiError = {message: "Network error. Please check your connection."};
            return Promise.reject(apiError);
        }

        const apiError: ApiError = {
            message: error?.message ?? "Unexpected server error"
        };
        return Promise.reject(apiError);
    }
);

//GENERAL USER//

export const login = (email: string, password: string) =>
    api.post("/auth/login", {email, password}).then(response => {
        localStorage.setItem("accessToken", response.data.accessToken);
        localStorage.setItem("refreshToken", response.data.refreshToken);
        return response.data;
    });

export const changePassword = (oldPassword: string, newPassword: string) =>
    api.put("/profile/password", {oldPassword, newPassword});

export const getMyProfile = () =>
    api.get<UserProfile>("/profile/me").then(response => response.data);

export const updateMyProfile = (fullName: string) =>
    api.put<UserProfile>('/profile/me', {fullName}).then(response => response.data);

export const requestEmailOtp = (email: string) =>
    api.post("/auth/password/email-otp/request", {email}).then(r => r.data);

export const verifyEmailOtp = (email: string, otp: string) =>
    api.post("/auth/password/email-otp/verify", {email, otp}).then(r => r.data);

export const resetPassword = (
    resetSessionToken: string,
    newPassword: string,
    confirmPassword: string
) =>
    api.post("/auth/password/reset", {
        resetSessionToken,
        newPassword,
        confirmPassword
    }).then(r => r.data);


//ADMIN//

//Courses//
export const getCourses = (page: number = 0, size: number = 10, title: string = "") =>
    api.get("/admin/courses", {
        params: {
            page,
            size,
            ...(title?.trim() ? {title: title.trim()} : {}),
        },
    })
        .then(response => ({
            courses: response.data.content || [],
            totalPages: response.data.page.totalPages || 1
        }));

export const getAdminCourseDetails = (courseId: number, page: number = 0, size: number = 10) =>
    api.get<CourseDetailsResponse>(`/admin/courses/${courseId}`, {params: {page, size}})
        .then(res => res.data);

export const createCourse = (course: CourseRequest) =>
    api.post<Course>("/admin/courses", course);

export const updateCourse = (id: string, course: CourseRequest) =>
    api.put<Course>(`/admin/courses/${id}`, course);

export const deleteCourse = (id: string) =>
    api.delete(`/admin/courses/${id}`);

export const getCourseById = (courseId: number) =>
    api
        .get<Course>(`/admin/courses/course/${courseId}`)
        .then((response) => response.data);

// Chapters
export const createChapter = (chapter: Omit<Chapter, "id">) =>
    api.post<Chapter>(`/admin/chapters`, chapter);

export const updateChapter = (id: string, chapter: Omit<Chapter, "id">) =>
    api.put<Chapter>(`/admin/chapters/${id}`, chapter);

export const deleteChapter = (id: string) =>
    api.delete(`/admin/chapters/${id}`);

export const getAdminChapterDetails = (chapterId: number, page: number = 0, size: number = 10) =>
    api.get<ChapterDetailsResponse>(`/admin/chapters/${chapterId}`, {params: {page, size}})
        .then(res => res.data);

//Lessons//
export const createLesson = (lesson: Omit<Lesson, "id">) =>
    api.post<Lesson>(`/admin/lessons`, lesson);
export const updateLesson = (id: string, lesson: Omit<Lesson, "id">) =>
    api.put<Lesson>(`/admin/lessons/${id}`, lesson);
export const deleteLesson = (id: string) =>
    api.delete(`/admin/lessons/${id}`);
export const getAdminLessonDetailsResponse = (lessonId: number) =>
    api.get<LessonDetailsResponse>(`/admin/lessons/${lessonId}`)
        .then(response => response.data);

export const getUserById = (id: number) => {
    return api
        .get(`/admin/users/${id}`)
        .then(res => res.data as UserProfileResponse);
};

//Code Tasks//
export const getCodeTasksList = (lessonId: number) =>
    api
        .get<CodeTaskListResponse>(`/admin/lessons/code-tasks/${lessonId}`)
        .then(res => res.data);


export const executeAdminCode = (code: string) =>
    api.post<CodeExecutionResponse>(`/admin/test/execute`, {code})
        .then(res => res.data);

export const getCodeTaskById = (id: number) =>
    api.get<CodeTaskAttemptResponse>(`/admin/lessons/code-tasks/details/${id}`)
        .then(res => res.data);

export const executeCodeTask = (taskId: number, code: string) =>
    api.post<CodeExecutionResponse>(`/admin/lessons/code-tasks/execute`, {
        taskId,
        code,
    })
        .then(res => res.data);

export const createCodeTask = (lessonId: number, req: CodeTaskRequest) => {
    return api.post(`/admin/lessons/code-tasks/${lessonId}`, req).then((r) => r.data);
};

export const updateCodeTask = (taskId: number, req: CodeTaskRequest) => {
    return api.put(`/admin/lessons/code-tasks/${taskId}`, req).then((r) => r.data);
};

export const getCodeTaskDetails = (taskId: number) => {
    return api.get(`/admin/lessons/code-tasks/task-details/${taskId}`).then((r) => r.data);
};

export const deleteCodeTask = (taskId: number) => {
    return api.delete(`/admin/lessons/code-tasks/${taskId}`);
};

export const getPracticeTasksList = (lessonId: number) => {
    return api
        .get(`/admin/lessons/practice-tasks/${lessonId}`)
        .then((r) => r.data as PracticeTaskListResponse);
};

export const getPracticeTaskDetails = (taskId: number) => {
    return api
        .get(`/admin/lessons/practice-tasks/task-details/${taskId}`)
        .then((r) => r.data as PracticeTaskDetailsResponse);
};

export const createPracticeTask = (lessonId: number, body: CreatePracticeTaskRequest) => {
    return api.post(`/admin/lessons/practice-tasks/${lessonId}`, body).then((r) => r.data);
};

export const updatePracticeTask = (taskId: number, body: UpdatePracticeTaskRequest) => {
    return api.put(`/admin/lessons/practice-tasks/${taskId}`, body).then((r) => r.data);
};

export const deletePracticeTask = (taskId: number) => {
    return api.delete(`/admin/lessons/practice-tasks/${taskId}`).then((r) => r.data);
};

export const getAdminCodeTaskAttempts = (
    taskId: number,
    page: number,
    size: number,
    email?: string,
    streamIds?: number[]
) => {
    return api
        .get(`/admin/lessons/code-tasks/attempts-list/${taskId}`, {
            params: {
                page,
                size,
                ...(email && email.trim() ? {email: email.trim()} : {}),
                ...(streamIds && streamIds.length
                    ? {streamIds: streamIds.join(",")}
                    : {}),
            },
        })
        .then((r) => r.data);
};

export const getAdminUserAnalytics = async (userId: number): Promise<AdminUserAnalyticsResponse> => {
    const res = await api.get(`/admin/users/${userId}/analytics`);
    return res.data;
};

export const getAdminUserAttempts = async (
    userId: number,
    page: number,
    size: number
) => {
    const res = await api.get(`/admin/users/${userId}/attempts`, {
        params: {page, size},
    });
    return res.data;
};

export const resetCodeTaskAttempts = (taskId: number) => {
    return api
        .delete(`/admin/lessons/code-tasks/${taskId}/attempts/reset`)
        .then((r) => r.data);
};

export const exportCodeTasksByLesson = async (lessonId: number): Promise<Blob> => {
    const response = await api.get(`/admin/lessons/code-tasks/${lessonId}/export`, {
        responseType: "blob",
    });

    return response.data;
};

export const importCodeTasksByLesson = async (
    lessonId: number,
    file: File
): Promise<CodeTaskImportResultResponse> => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post<CodeTaskImportResultResponse>(
        `/admin/lessons/code-tasks/${lessonId}/import`,
        formData
    );

    return response.data;
};

//Users//
export const getUserRoles = () =>
    api.get(`/admin/users/roles`).then(res => res.data as UserRole[]);

export const createUser = (req: CreateUserRequest) =>
    api.post(`/admin/users`, req).then(res => res.data as UserProfileResponse);

export const updateUser = (req: UpdateUserRequest) =>
    api.put(`/admin/users`, req).then(res => res.data as UserProfileResponse);

export const deleteUser = (id: number) =>
    api.delete(`/admin/users/${id}`).then(res => res.data);

export const getCodeAttemptByTaskId = (taskId: number) =>
    api.get<CodeAttempt>(`/admin/lessons/code-tasks/attempts/${taskId}`)
        .then(res => res.data);

export const getUsers = (page: number = 0, size: number = 10, email: string = "") => {
    return api
        .get(`/admin/users`, {
            params: {
                page,
                size,
                ...(email?.trim() ? {email: email.trim()} : {}),
            },
        })
        .then((response) => ({
            users: response.data.content || [],
            totalPages: response.data.totalPages ?? response.data.page?.totalPages ?? 1,
        }));
};

export const getCourseAccessUsers = (
    courseId: number,
    page: number = 0,
    size: number = 10,
    email: string = ""
) => {
    return api
        .get(`/admin/course-users`, {
            params: {
                courseId,
                email,
                page,
                size,
            },
        })
        .then((response) => ({
            users: response.data.content || [],
            totalPages: response.data.totalPages ?? response.data.page?.totalPages ?? 1,
        }));
};

export const getCourseAccessAvailableUsers = (
    courseId: number,
    page: number = 0,
    size: number = 10,
    email: string = ""
) => {
    return api
        .get(`/admin/course-users/available`, {params: {courseId, email, page, size}})
        .then((response) => ({
            users: response.data.content || [],
            totalPages: response.data.totalPages ?? response.data.page?.totalPages ?? 1,
        }));
};

export const assignUserToCourse = (courseId: number, userId: number) => {
    return api.post(`/admin/course-users/assign`, null, {params: {courseId, userId}});
};

export const unassignUserFromCourse = (courseId: number, userId: number) => {
    return api.delete(`/admin/course-users/unassign`, {params: {courseId, userId}});
};

//Streams//

export const getStreams = (page: number = 0, size: number = 10, name: string = "") => {
    return api
        .get(`/admin/streams`, {params: {page, size, name}})
        .then((response) => ({
            streams: response.data.content || [],
            totalPages: response.data.totalPages ?? response.data.page?.totalPages ?? 1,
        }));
};

export const getMyStreams = (page: number = 0, size: number = 10, name: string = "") => {
    return api
        .get(`/streams/my`, {params: {page, size, name}})
        .then((response) => ({
            streams: response.data.content || [],
            totalPages: response.data.totalPages ?? response.data.page?.totalPages ?? 1,
        }));
};

export const createStream = (req: { name: string; startingDate: string; endingDate?: string | null }) => {
    return api.post(`/admin/streams`, req).then((r) => r.data);
};

export const getStreamById = (id: number) => {
    return api.get(`/admin/streams/${id}`).then((r) => r.data);
};

export const getMyStreamById = (id: number) => {
    return api.get(`/streams/my/${id}`).then((r) => r.data);
};

export const updateStream = (req: { id: number; name: string; startingDate: string; endingDate?: string | null }) => {
    return api.put(`/admin/streams/${req.id}`, req).then((r) => r.data);
};

export const deleteStream = (id: number) => {
    return api.delete(`/admin/streams/${id}`).then((r) => r.data);
};

export const getStreamUsers = (streamId: number, page = 0, size = 10, email = "") => {
    return api
        .get(`/admin/streams/users/${streamId}`, {params: {page, size, email}})
        .then((response) => ({
            users: response.data.content || [],
            totalPages: response.data.totalPages ?? response.data.page?.totalPages ?? 1,
        }));
};

export const assignUserToStream = (streamId: number, userId: number) => {
    return api
        .post(`/admin/streams/assign`, null, {params: {streamId, userId}})
        .then((r) => r.data);
};

export const unassignUserFromStream = (streamId: number, userId: number) => {
    return api
        .delete(`/admin/streams/delete`, {params: {streamId, userId}})
        .then((r) => r.data);
};

export const getStreamAvailableUsers = (
    streamId: number,
    page: number = 0,
    size: number = 10,
    email: string = "") => {
    return api
        .get(`/admin/streams/available-users/${streamId}`, {params: {page, size, email}})
        .then((response) => ({
            users: response.data.content || [],
            totalPages: response.data.totalPages ?? response.data.page?.totalPages ?? 1,
        }));
};

export const getTeacherCodeTaskAttempts = (
    taskId: number,
    page: number,
    size: number,
    email?: string,
    streamIds?: number[]
) => {
    return api
        .get(`/lessons/code-tasks/attempts-list/${taskId}`, {
            params: {
                page,
                size,
                ...(email && email.trim() ? {email: email.trim()} : {}),
                ...(streamIds && streamIds.length ? {streamIds: streamIds.join(",")} : {}),
            },
        })
        .then((r) => r.data);
};

//Schedule//

export const getStreamWeeklySchedule = (streamId: number, weekStart: string) => {
    return api
        .get(`/admin/streams/schedule/${streamId}/weekly`, {params: {weekStart}})
        .then((r) => r.data);
};

export const createStreamScheduleSlot = (streamId: number, req: any) => {
    return api.post(`/admin/streams/schedule/${streamId}/slots`, req).then((r) => r.data);
};

export const updateStreamScheduleSlot = (streamId: number, slotId: number, req: any) => {
    return api.put(`/admin/streams/schedule/${streamId}/slots/${slotId}`, req).then((r) => r.data);
};

export const deleteStreamScheduleSlot = (streamId: number, slotId: number) => {
    return api.delete(`/admin/streams/schedule/${streamId}/slots/${slotId}`).then((r) => r.data);
};

//Course Logo//

export const getCourseLogos = (
    page: number = 0,
    size: number = 10,
    name: string = ""
) => {
    return api
        .get("/admin/course-logo", {
            params: {
                page,
                size,
                name,
            },
        })
        .then((response) => ({
            logos: response.data.content || [],
            totalPages:
                response.data.totalPages ??
                response.data.page?.totalPages ??
                1,
        }));
};

export const getCourseLogoById = async (
    id: number
): Promise<CourseLogo> => {
    const response = await api.get(`/admin/course-logo/${id}`);
    return response.data;
};

export const createCourseLogo = async (
    request: CourseLogoRequest
): Promise<CourseLogo> => {
    const response = await api.post("/admin/course-logo", request);
    return response.data;
};

export const updateCourseLogo = async (
    id: number,
    request: CourseLogoRequest
): Promise<CourseLogo> => {
    const response = await api.put(`/admin/course-logo/${id}`, request);
    return response.data;
};

export const deleteCourseLogo = async (
    id: number
): Promise<void> => {
    await api.delete(`/admin/course-logo/${id}`);
};

//Company//

//Companies//

export const getCompanies = (page: number = 0, size: number = 10) => {
    return api
        .get(`/admin/companies`, {params: {page, size}})
        .then((response) => ({
            companies: response.data.content || [],
            totalPages: response.data.totalPages ?? response.data.page?.totalPages ?? 1,
        }));
};

export const getCompanyById = (id: number) => {
    return api.get<Company>(`/admin/companies/${id}`).then((r) => r.data);
};

export const createCompany = (req: CompanyRequest) => {
    return api.post<Company>(`/admin/companies`, req).then((r) => r.data);
};

export const updateCompany = (id: number, req: CompanyRequest) => {
    return api.put<Company>(`/admin/companies/${id}`, req).then((r) => r.data);
};

export const deleteCompany = (id: number) => {
    return api.delete(`/admin/companies/${id}`).then((r) => r.data);
};

export const activateCompany = (id: number) => {
    return api.patch<Company>(`/admin/companies/${id}/activate`).then((r) => r.data);
};

export const deactivateCompany = (id: number) => {
    return api.patch<Company>(`/admin/companies/${id}/deactivate`).then((r) => r.data);
};

export const getCompanyUsers = async (
    companyId: number,
    page: number,
    size: number,
    email?: string
) => {
    const response = await api.get(`/admin/companies/${companyId}/users`, {
        params: {
            page,
            size,
            email: email || undefined,
        },
    });

    return {
        users: response.data.content,
        totalPages: response.data.totalPages ?? response.data.page?.totalPages ?? 1,
        totalElements: response.data.totalElements ?? response.data.page?.totalElements,
    };
};

export const getCompanyUserById = async (
    companyId: number,
    userId: number
) => {
    const response = await api.get(`/admin/companies/${companyId}/users/${userId}`);
    return response.data;
};

export const createCompanyUser = async (
    companyId: number,
    request: any
) => {
    const response = await api.post(`/admin/companies/${companyId}/user`, request);
    return response.data;
};

export const updateCompanyUser = async (
    companyId: number,
    request: any
) => {
    const response = await api.put(
        `/admin/companies/${companyId}/users`,
        request
    );
    return response.data;
};

export const deleteCompanyUser = async (
    companyId: number,
    userId: number
) => {
    await api.delete(`/admin/companies/${companyId}/users/${userId}`);
};

export const getCompanyUserRoles = () =>
    api.get(`/company/admin/users/roles`).then(res => res.data as UserRole[]);

//STUDENT//

//Courses//
export const getMyCourses = (page: number = 0, size: number = 10) =>
    api.get("/courses/my", {params: {page, size}})
        .then((response) => ({
            courses: response.data.content || [],
            totalPages: response.data.page?.totalPages ?? response.data.totalPages ?? 1,
        }));

export const getMyCourseDetails = (courseId: number, page: number = 0, size: number = 10) =>
    api.get<CourseDetailsResponse>(`/courses/details/${courseId}`, {params: {page, size}})
        .then(res => res.data);

//Chapters//
export const getMyChapterDetails = (chapterId: number, page: number = 0, size: number = 10) =>
    api.get<ChapterDetailsResponse>(`/chapters/details/${chapterId}`, {params: {page, size}})
        .then(res => res.data);
//Lessons//
export const getLessonByIdAndCurrentUser = (lessonId: number) =>
    api.get<LessonDetailsResponse>(`/lessons/${lessonId}`)
        .then(response => response.data);

//Code Tasks//
export const getMyCodeTasksByLesson = (lessonId: number) =>
    api
        .get<CodeTaskListResponse>(`/lessons/code-tasks/${lessonId}`)
        .then(res => res.data);


export const getMyCodeTaskById = (id: number) =>
    api
        .get<CodeTaskAttemptResponse>(`/lessons/code-tasks/details/${id}`)
        .then(res => res.data);

export const executeMyCodeTask = (taskId: number, code: string) =>
    api
        .post<CodeExecutionResponse>(`/lessons/code-tasks/execute`, {
            taskId,
            code,
        })
        .then(res => res.data);

export const resetMyCodeTaskAttempts = (taskId: number) => {
    return api
        .delete(`/lessons/code-tasks/${taskId}/attempts/reset`)
        .then((r) => r.data);
};

export const getMyPracticeTasksByLesson = (lessonId: number) => {
    return api
        .get(`/lessons/practice-tasks/${lessonId}`)
        .then((r) => r.data as PracticeTaskListResponse);
};

export const getMyPracticeTaskById = (taskId: number) => {
    return api
        .get(`/lessons/practice-tasks/details/${taskId}`)
        .then((r) => r.data as PracticeTaskDetailsResponse);
};

export const getMyStreamUsers = (streamId: number, page = 0, size = 10, email = "") => {
    return api
        .get(`/streams/my/${streamId}/users`, {params: {page, size, email}})
        .then((response) => ({
            users: response.data.content || [],
            totalPages: response.data.totalPages ?? response.data.page?.totalPages ?? 1,
        }));
};

export const getMyStreamWeeklySchedule = (streamId: number, weekStart: string) => {
    return api
        .get(`/streams/schedule/my/${streamId}/weekly`, {params: {weekStart}})
        .then((r) => r.data);
};

export const getMyWeeklyScheduleSummary = (weekStartISO: string) => {
    return api
        .get(`/streams/schedule/my/weekly`, {params: {weekStart: weekStartISO}})
        .then((r) => r.data);
};

export const getTeacherUserAnalytics = async (userId: number): Promise<AdminUserAnalyticsResponse> => {
    const res = await api.get(`/teacher/users/${userId}/analytics`);
    return res.data;
};

export const getTeacherUserAttempts = async (
    userId: number,
    page: number,
    size: number
) => {
    const res = await api.get(`/teacher/users/${userId}/attempts`, {
        params: {page, size},
    });
    return res.data;
};